import React, { useState, useMemo } from 'react';
import Tabs from '@salesforce/design-system-react/components/tabs';
import TabsPanel from '@salesforce/design-system-react/components/tabs/panel';
import Icon from '@salesforce/design-system-react/components/icon';
import { colors, type, space, radius, formatMoney, relativeDate } from '../shared/tokens';
import sampleData from '../shared/sampleData';

import DonorHero from '../shared/components/DonorHero';
import KPIStrip from '../shared/components/KPIStrip';
import UnifiedTimeline from '../shared/components/UnifiedTimeline';
import MovesFunnel from '../shared/components/MovesFunnel';
import NextBestActionCard from '../shared/components/NextBestActionCard';
import SectionCard from '../shared/components/SectionCard';
import InlineEditField from '../shared/components/InlineEditField';
import Avatar from '../shared/components/Avatar';
import GivingHeatmap from '../shared/components/GivingHeatmap';
import StatusChipStrip from '../shared/components/StatusChipStrip';
import { movesConfig } from '../shared/icons';

// ───────────────────────────────────────────────────────────────────────────
// Left rail
// ───────────────────────────────────────────────────────────────────────────

const LeftRail = ({ contact, household }) => {
  const members = household.memberIds
    .map(id => Object.values(sampleData.contacts).find(c => c.id === id))
    .filter(Boolean);

  return (
    <aside
      style={{
        width: 260,
        flexShrink: 0,
        padding: space[4],
        display: 'flex',
        flexDirection: 'column',
        gap: space[3],
        borderRight: `1px solid ${colors.border}`,
        background: colors.surface,
        overflowY: 'auto',
      }}
    >
      <SectionCard title="Contact info" compact>
        <InlineEditField label="Email" value={contact.email} isLink />
        <InlineEditField label="Phone" value={contact.phone} />
        <InlineEditField label="Mobile" value={contact.mobile || ''} />
        <InlineEditField label="Preferred name" value={contact.preferredName} />
        <InlineEditField label="Pronouns" value={contact.pronouns} />
      </SectionCard>

      <SectionCard title="Household" compact>
        <div style={{ display: 'flex', alignItems: 'center', gap: space[2], marginBottom: space[2] }}>
          <Icon category="standard" name="household" size="x-small" style={{ fill: colors.brand }} />
          <span style={{ ...type.small, fontWeight: 600, color: colors.brand, cursor: 'pointer' }}>
            {household.name}
          </span>
        </div>
        <div style={{ display: 'flex', gap: space[1], flexWrap: 'wrap' }}>
          {members.map(m => (
            <div key={m.id} title={`${m.firstName} ${m.lastName} — ${household.roles[m.id]}`}>
              <Avatar initials={m.avatarInitials} color={m.avatarColor} size="sm" />
            </div>
          ))}
        </div>
        <div style={{ ...type.micro, color: colors.textTertiary, marginTop: space[2] }}>
          Household lifetime · {formatMoney(household.householdLifetime, { compact: true })}
        </div>
      </SectionCard>

      <SectionCard title="Addresses" compact collapsible defaultOpen>
        {household.addresses.map(a => (
          <div key={a.id} style={{ marginBottom: space[3] }}>
            <div style={{ ...type.small, color: colors.textPrimary, lineHeight: 1.4 }}>
              {a.street}<br />
              {a.city}, {a.state} {a.postalCode}
            </div>
            <div style={{ display: 'flex', gap: space[1], marginTop: space[1], flexWrap: 'wrap' }}>
              {a.isDefault && (
                <span style={{
                  ...type.micro, background: colors.brandMuted, color: colors.brand,
                  padding: '1px 6px', borderRadius: radius.pill, fontWeight: 600,
                }}>Primary</span>
              )}
              {a.seasonalRange && (
                <span style={{
                  ...type.micro, background: colors.surfaceInk, color: colors.textSecondary,
                  padding: '1px 6px', borderRadius: radius.pill, fontWeight: 600,
                }}>
                  {seasonalLabel(a.seasonalRange)}
                </span>
              )}
            </div>
          </div>
        ))}
      </SectionCard>

      <SectionCard title="Tags" compact>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: space[1] }}>
          {contact.tags.map(t => (
            <span key={t} style={{
              ...type.micro, background: colors.surfaceInk, color: colors.textSecondary,
              padding: '2px 8px', borderRadius: radius.pill, fontWeight: 600,
            }}>
              {t}
            </span>
          ))}
        </div>
      </SectionCard>

      <SectionCard title="Communications" compact>
        {Object.entries(contact.commsPrefs).map(([k, v]) => (
          <div key={k} style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: `${space[1]}px 0`,
            ...type.small,
          }}>
            <span style={{ color: colors.textSecondary, textTransform: 'capitalize' }}>{k}</span>
            <span style={{
              color: v ? colors.success : colors.textTertiary, fontWeight: 700,
            }}>
              {v ? '✓ Opted in' : '— Opted out'}
            </span>
          </div>
        ))}
      </SectionCard>

      <SectionCard title="Source" compact>
        <div style={{ ...type.small, color: colors.textPrimary, fontWeight: 600 }}>
          {contact.source.label}
        </div>
        <div style={{ ...type.micro, color: colors.textTertiary, marginTop: 2 }}>
          {contact.source.type}
        </div>
      </SectionCard>
    </aside>
  );
};

const seasonalLabel = (range) => {
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[range.startMonth - 1]}–${months[range.endMonth - 1]}`;
};

// ───────────────────────────────────────────────────────────────────────────
// Right rail
// ───────────────────────────────────────────────────────────────────────────

const RightRail = ({ contact, action, onMove }) => (
  <aside
    style={{
      width: 300,
      flexShrink: 0,
      padding: space[4],
      display: 'flex',
      flexDirection: 'column',
      gap: space[3],
      background: colors.surfaceInk,
      overflowY: 'auto',
    }}
  >
    <SectionCard title="Moves management" compact padded>
      <MovesFunnel
        current={contact.movesStage}
        onChange={onMove}
        orientation="horizontal"
        compact
      />
      <div style={{ ...type.micro, color: colors.textTertiary, marginTop: space[2], textAlign: 'center' }}>
        Currently in <strong style={{ color: colors.textSecondary }}>
          {movesConfig.find(m => m.id === contact.movesStage)?.label}
        </strong>
      </div>
    </SectionCard>

    <NextBestActionCard
      action={action}
      onPrimary={() => alert('Demo: log call')}
      onSecondary={() => alert('Demo: snooze action')}
    />

    {contact.solicitor && (
      <SectionCard title="Solicitor" compact>
        <div style={{ display: 'flex', alignItems: 'center', gap: space[2] }}>
          <Avatar
            initials={contact.solicitor.avatarInitials}
            color={contact.solicitor.avatarColor}
            size="sm"
          />
          <div>
            <div style={{ ...type.small, fontWeight: 600, color: colors.textPrimary }}>
              {contact.solicitor.name}
            </div>
            <div style={{ ...type.micro, color: colors.textTertiary }}>
              {contact.solicitor.role}
            </div>
          </div>
        </div>
      </SectionCard>
    )}

    <SectionCard title="AI copilot" compact>
      <div style={{
        ...type.small, color: colors.textTertiary, lineHeight: 1.5, marginBottom: space[2],
      }}>
        Draft a thank-you · Summarize giving history · Suggest ask amount
      </div>
      <button
        disabled
        style={{
          width: '100%',
          padding: `${space[2]}px ${space[3]}px`,
          background: colors.brandMuted,
          color: colors.brand,
          border: `1px dashed ${colors.brand}`,
          borderRadius: radius.sm,
          ...type.small,
          fontWeight: 600,
          fontFamily: 'inherit',
          cursor: 'not-allowed',
          opacity: 0.75,
        }}
      >
        Open drawer · coming in v2
      </button>
    </SectionCard>
  </aside>
);

// ───────────────────────────────────────────────────────────────────────────
// Giving tab content
// ───────────────────────────────────────────────────────────────────────────

const GivingTab = ({ contact }) => {
  const gifts = sampleData.giftHistory.slice().sort((a, b) => new Date(b.date) - new Date(a.date));
  const softCredits = sampleData.softCredits;

  return (
    <>
      <SectionCard title="Giving activity · last 52 weeks">
        <GivingHeatmap gifts={gifts} />
      </SectionCard>

      <SectionCard
        title="Gift history"
        subtitle={`${gifts.length} gifts · ${formatMoney(contact.lifetimeGiving)} lifetime`}
      >
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', ...type.label, color: colors.textTertiary }}>
                <th style={{ padding: space[2], borderBottom: `1px solid ${colors.border}` }}>Date</th>
                <th style={{ padding: space[2], borderBottom: `1px solid ${colors.border}` }}>Amount</th>
                <th style={{ padding: space[2], borderBottom: `1px solid ${colors.border}` }}>Type</th>
                <th style={{ padding: space[2], borderBottom: `1px solid ${colors.border}` }}>Campaign</th>
              </tr>
            </thead>
            <tbody>
              {gifts.slice(0, 15).map(g => (
                <tr key={g.id} style={{ ...type.small }}>
                  <td style={{ padding: space[2], borderBottom: `1px solid ${colors.border}`, color: colors.textSecondary }}>
                    {new Date(g.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </td>
                  <td style={{ padding: space[2], borderBottom: `1px solid ${colors.border}`, fontWeight: 700, color: colors.timeline.gift }}>
                    {formatMoney(g.amount)}
                  </td>
                  <td style={{ padding: space[2], borderBottom: `1px solid ${colors.border}`, color: colors.textSecondary }}>
                    {g.type}
                  </td>
                  <td style={{ padding: space[2], borderBottom: `1px solid ${colors.border}`, color: colors.brand, cursor: 'pointer' }}>
                    {g.campaign}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>

      <SectionCard title="Soft credits" subtitle={`${formatMoney(contact.softCreditTotal)} lifetime`}>
        {softCredits.map(sc => (
          <div key={sc.id} style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: `${space[2]}px 0`,
            borderBottom: `1px dashed ${colors.border}`,
          }}>
            <div>
              <div style={{ ...type.small, fontWeight: 600, color: colors.textPrimary }}>
                {sc.donorName}
              </div>
              <div style={{ ...type.micro, color: colors.textTertiary }}>
                {relativeDate(sc.date)} · {sc.role}
              </div>
            </div>
            <div style={{ ...type.small, fontWeight: 700, color: colors.timeline.gift }}>
              {formatMoney(sc.amount)}
            </div>
          </div>
        ))}
      </SectionCard>
    </>
  );
};

// ───────────────────────────────────────────────────────────────────────────
// Recurring tab content (summary; the full page is its own mockup)
// ───────────────────────────────────────────────────────────────────────────

const RecurringTab = () => {
  const rd = sampleData.recurringDonation;
  const paid = rd.installments.filter(i => i.status === 'Paid').length;
  const scheduled = rd.installments.length - paid;

  return (
    <SectionCard
      title={rd.name}
      subtitle={`${formatMoney(rd.amount)} / ${rd.period.toLowerCase()} since ${new Date(rd.startDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`}
      action={
        <span style={{
          ...type.small, color: colors.success, fontWeight: 700,
        }}>
          ● {rd.status}
        </span>
      }
    >
      <div style={{ display: 'flex', gap: space[4], flexWrap: 'wrap', marginBottom: space[3] }}>
        <div>
          <div style={{ ...type.label, color: colors.textTertiary }}>MRR</div>
          <div style={{ ...type.kpi, color: colors.textPrimary }}>{formatMoney(rd.amount)}</div>
        </div>
        <div>
          <div style={{ ...type.label, color: colors.textTertiary }}>Committed annual</div>
          <div style={{ ...type.kpi, color: colors.textPrimary }}>{formatMoney(rd.committedAnnualValue)}</div>
        </div>
        <div>
          <div style={{ ...type.label, color: colors.textTertiary }}>Lifetime from recurring</div>
          <div style={{ ...type.kpi, color: colors.textPrimary }}>{formatMoney(rd.lifetimeFromRecurring)}</div>
        </div>
        <div>
          <div style={{ ...type.label, color: colors.textTertiary }}>On-time streak</div>
          <div style={{ ...type.kpi, color: colors.success }}>{rd.onTimeStreak}</div>
        </div>
      </div>
      <div style={{ ...type.small, color: colors.textSecondary, marginBottom: space[2] }}>
        {paid} paid · {scheduled} scheduled in next 12 months
      </div>
      <div style={{
        padding: space[3],
        background: colors.surfaceInk,
        borderRadius: radius.sm,
        ...type.small,
        color: colors.textTertiary,
      }}>
        Open the <strong style={{ color: colors.brand }}>Recurring Donation</strong> mockup from the left nav to see the full
        schedule strip, pause / upgrade affordances, and allocation breakdown.
      </div>
    </SectionCard>
  );
};

// ───────────────────────────────────────────────────────────────────────────
// Insights tab — score breakdown as a dedicated surface
// ───────────────────────────────────────────────────────────────────────────

const InsightsTab = ({ contact }) => {
  const breakdown = contact.scoreBreakdown || {};
  return (
    <SectionCard
      title="Donor score breakdown"
      subtitle={`Composite score ${contact.donorScore}/100`}
    >
      {Object.entries(breakdown).map(([dim, val]) => (
        <div key={dim} style={{ marginBottom: space[4] }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between', marginBottom: space[1],
            ...type.body,
          }}>
            <span style={{ color: colors.textPrimary, textTransform: 'capitalize', fontWeight: 600 }}>{dim}</span>
            <span style={{ fontWeight: 700 }}>{val}</span>
          </div>
          <div style={{ height: 8, background: colors.surfaceInk, borderRadius: 4, overflow: 'hidden' }}>
            <div style={{
              width: `${val}%`, height: '100%',
              background: val >= 70 ? colors.success : val >= 40 ? colors.warning : colors.error,
              transition: 'width 240ms',
            }} />
          </div>
        </div>
      ))}
      <p style={{ ...type.small, color: colors.textTertiary, lineHeight: 1.5, marginTop: space[4] }}>
        Composite score derives from wealth indicators (capacity, real estate, public records),
        engagement signals (email opens, event attendance, volunteer hours, web visits),
        individual giving behavior (frequency, recency, amount), household co-attribution,
        and inclination modeling.
      </p>
    </SectionCard>
  );
};

// ───────────────────────────────────────────────────────────────────────────
// Main content
// ───────────────────────────────────────────────────────────────────────────

const DonorProfile = () => {
  const contact = sampleData.contacts.eleanor;
  const household = sampleData.household;
  const events = sampleData.timelineEvents;
  const action = sampleData.nextBestAction;

  const [moveStage, setMoveStage] = useState(contact.movesStage);

  const kpiTiles = useMemo(() => ([
    {
      id: 'lifetime',
      label: 'Lifetime giving',
      value: formatMoney(contact.lifetimeGiving, { compact: true }),
      unit: '',
      subValue: `since ${new Date(contact.firstGiftDate).getFullYear()}`,
      delta: 18,
      deltaLabel: '+18% YoY',
      sparkline: [42, 48, 55, 51, 63, 71, 84, 82, 96, 98],
      color: colors.brand,
    },
    {
      id: 'largest',
      label: 'Largest gift',
      value: formatMoney(contact.largestGift, { compact: true }),
      subValue: 'Year-End Match 2024',
    },
    {
      id: 'last',
      label: 'Last gift',
      value: formatMoney(contact.lastGiftAmount),
      subValue: relativeDate(contact.lastGiftDate),
      delta: 0,
      sparkline: [125, 125, 125, 500, 125, 125, 125, 500, 125, 500],
    },
    {
      id: 'count',
      label: 'Number of gifts',
      value: `${contact.numberOfGifts}`,
      delta: 12,
      deltaLabel: '+4 this year',
      sparkline: [8, 12, 15, 19, 23, 27, 30, 34, 37, 38],
    },
    {
      id: 'retention',
      label: 'Retention',
      value: `${contact.retentionYears}`,
      unit: 'years',
      subValue: 'Consecutive',
      delta: 1,
      deltaLabel: '+1 yr',
    },
    {
      id: 'mrr',
      label: 'Active MRR',
      value: formatMoney(contact.activeRecurringMonthly),
      subValue: 'Visa ••4242',
      color: colors.success,
    },
  ]), [contact]);

  return (
    <div style={{ display: 'flex', minHeight: '100%', background: colors.surfaceInk }}>
      <LeftRail contact={contact} household={household} />

      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <DonorHero contact={contact} onAction={(a) => console.log('quick-action', a)} sticky />

        <div style={{ flex: 1, padding: space[5], overflowY: 'auto' }}>
          <KPIStrip tiles={kpiTiles} />

          <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.md, overflow: 'hidden' }}>
            <Tabs variant="default" id="donor-profile-tabs">
              <TabsPanel label="Overview">
                <div style={{ padding: space[5] }}>
                  <UnifiedTimeline events={events} />
                </div>
              </TabsPanel>
              <TabsPanel label="Giving">
                <div style={{ padding: space[5] }}>
                  <GivingTab contact={contact} />
                </div>
              </TabsPanel>
              <TabsPanel label="Recurring">
                <div style={{ padding: space[5] }}>
                  <RecurringTab />
                </div>
              </TabsPanel>
              <TabsPanel label="Relationships">
                <div style={{ padding: space[5] }}>
                  <SectionCard
                    title="Relationships"
                    subtitle="Household · affiliations · soft-credit network"
                  >
                    <p style={{ ...type.body, color: colors.textSecondary, lineHeight: 1.6 }}>
                      Open the <strong style={{ color: colors.brand }}>Relationship Explorer</strong> from the
                      left nav for the full dagre-laid-out graph and card-list fallback.
                    </p>
                  </SectionCard>
                </div>
              </TabsPanel>
              <TabsPanel label="Engagement">
                <div style={{ padding: space[5] }}>
                  <SectionCard title="Engagement signals">
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: space[4] }}>
                      <EngagementStat label="Emails opened (90d)" value="28" delta={+14} />
                      <EngagementStat label="Events attended (12m)" value="4" delta={+1} />
                      <EngagementStat label="Volunteer hours (12m)" value="18" delta={+6} />
                      <EngagementStat label="Website visits (90d)" value="12" delta={-2} />
                    </div>
                  </SectionCard>
                </div>
              </TabsPanel>
              <TabsPanel label="Notes & Files">
                <div style={{ padding: space[5] }}>
                  <SectionCard title="Notes">
                    {events.filter(e => e.type === 'note').map(n => (
                      <div key={n.id} style={{ padding: `${space[3]}px 0`, borderBottom: `1px dashed ${colors.border}` }}>
                        <div style={{ ...type.small, fontWeight: 600, color: colors.textPrimary }}>{n.title}</div>
                        <div style={{ ...type.small, color: colors.textSecondary, marginTop: 2 }}>{n.description}</div>
                        <div style={{ ...type.micro, color: colors.textTertiary, marginTop: 4 }}>
                          {relativeDate(n.date)} · {n.actor}
                        </div>
                      </div>
                    ))}
                  </SectionCard>
                </div>
              </TabsPanel>
              <TabsPanel label="Tasks / Moves">
                <div style={{ padding: space[5] }}>
                  <SectionCard title="Open tasks & cultivation moves">
                    {events.filter(e => e.type === 'task').map(t => (
                      <div key={t.id} style={{
                        display: 'flex', gap: space[3], alignItems: 'flex-start',
                        padding: space[3],
                        border: `1px solid ${colors.border}`,
                        borderRadius: radius.sm,
                        marginBottom: space[2],
                      }}>
                        <input type="checkbox" style={{ marginTop: 2 }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ ...type.body, fontWeight: 600 }}>{t.title}</div>
                          <div style={{ ...type.small, color: colors.textTertiary, marginTop: 2 }}>
                            {t.description}
                          </div>
                        </div>
                        <StatusChipStrip chips={['volunteer']} size="sm" />
                      </div>
                    ))}
                  </SectionCard>
                </div>
              </TabsPanel>
              <TabsPanel label="Insights">
                <div style={{ padding: space[5] }}>
                  <InsightsTab contact={contact} />
                </div>
              </TabsPanel>
            </Tabs>
          </div>
        </div>
      </div>

      <RightRail
        contact={{ ...contact, movesStage: moveStage }}
        action={action}
        onMove={setMoveStage}
      />
    </div>
  );
};

const EngagementStat = ({ label, value, delta }) => (
  <div style={{
    background: colors.surface, padding: space[3],
    border: `1px solid ${colors.border}`, borderRadius: radius.md,
  }}>
    <div style={{ ...type.label, color: colors.textTertiary }}>{label}</div>
    <div style={{ ...type.kpi, color: colors.textPrimary, marginTop: 2 }}>{value}</div>
    <div style={{
      ...type.small, fontWeight: 600,
      color: delta > 0 ? colors.success : delta < 0 ? colors.error : colors.textTertiary,
      marginTop: 2,
    }}>
      {delta > 0 ? '▲' : delta < 0 ? '▼' : '—'} {Math.abs(delta)}
    </div>
  </div>
);

export default DonorProfile;
